import React from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { 
  FacebookIcon, 
  InstagramIcon, 
  TwitterIcon, 
  LinkedinIcon, 
  PhoneIcon, 
  MailIcon, 
  ArrowUpIcon,
  MapPinIcon,
  HotelIcon,
  AuctionIcon,
  EventIcon,
  ExperienceIcon,
  HomeIcon
} from '../../utils/IconWrapper';
import { useLanguage } from '../../i18n/LanguageContext';

const FooterContainer = styled.footer`
  background-color: var(--bg-secondary);
  padding: 5rem 2rem 2rem;
  position: relative;
`;

const FooterInner = styled.div`
  max-width: 1400px;
  margin: 0 auto;
`;

const TopSection = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 3rem;
  margin-bottom: 4rem;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const FooterColumn = styled.div`
  display: flex;
  flex-direction: column;
`;

const FooterLogo = styled.div`
  margin-bottom: 1.5rem;
  
  img {
    height: 40px;
    width: auto;
  }
`;

const ColumnTitle = styled.h4`
  font-size: 1.2rem;
  margin-bottom: 1.5rem;
  position: relative;
  display: inline-block;
  
  &:after {
    content: '';
    position: absolute;
    bottom: -10px;
    left: 0;
    width: 40px;
    height: 2px;
    background-color: var(--accent-primary);
  }
`;

const ColumnContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const FooterLink = styled(Link)`
  color: var(--text-secondary);
  transition: color 0.3s ease;
  font-size: 0.9rem;
  
  &:hover {
    color: var(--text-primary);
  }
`;

const ContactItem = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 1rem;
  margin-bottom: 1rem;
  color: var(--text-secondary);
  
  svg {
    margin-top: 4px;
    min-width: 18px;
  }
`;

const ContactText = styled.span`
  font-size: 0.9rem;
  line-height: 1.6;
`;

const SocialLinks = styled.div`
  display: flex;
  gap: 1rem;
  margin-top: 1rem;
`;

const SocialIcon = styled.a`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  border: 1px solid var(--text-secondary);
  color: var(--text-secondary);
  transition: all 0.3s ease;
  font-size: 1.2rem;
  
  &:hover {
    background-color: var(--accent-primary);
    border-color: var(--accent-primary);
    color: white;
  }
`;

const Divider = styled.div`
  height: 1px;
  background-color: rgba(255, 255, 255, 0.1);
  margin-bottom: 2rem;
`;

const BottomSection = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  
  @media (max-width: 768px) {
    flex-direction: column;
    gap: 1rem;
    text-align: center;
  }
`;

const Copyright = styled.p`
  color: var(--text-secondary);
  font-size: 0.8rem;
  margin: 0;
`;

const LegalLinks = styled.div`
  display: flex;
  gap: 1.5rem;
  
  @media (max-width: 768px) {
    flex-direction: column;
    gap: 0.5rem;
  }
`;

const LegalLink = styled(Link)`
  color: var(--text-secondary);
  font-size: 0.8rem;
  transition: color 0.3s ease;
  
  &:hover {
    color: var(--text-primary);
  }
`;

const BackToTopButton = styled.button`
  position: absolute;
  right: 2rem;
  top: -20px;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background-color: var(--accent-primary);
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  border: none;
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:hover {
    background-color: var(--accent-secondary);
    transform: translateY(-5px);
  }
`;

const Footer: React.FC = () => {
  const { t, language } = useLanguage();
  
  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  };
  
  return (
    <FooterContainer>
      <FooterInner>
        <TopSection>
          <FooterColumn>
            <FooterLogo>
              <img src="/logo.svg" alt="J-O-H Logo" />
            </FooterLogo>
            <ColumnContent>
              <p style={{ color: 'var(--text-secondary)', fontSize: '0.9rem', lineHeight: '1.6' }}>
                {language === 'it' 
                  ? 'Offriamo alloggi di lusso e servizi specializzati per la gestione immobiliare.' 
                  : 'We offer luxury accommodations and specialized services for property management.'}
              </p>
              <SocialLinks>
                <SocialIcon href="https://facebook.com" target="_blank" rel="noopener noreferrer">
                  <FacebookIcon />
                </SocialIcon>
                <SocialIcon href="https://www.instagram.com/joh.italia/" target="_blank" rel="noopener noreferrer">
                  <InstagramIcon />
                </SocialIcon>
              </SocialLinks>
            </ColumnContent>
          </FooterColumn>
          
          <FooterColumn>
            <ColumnTitle>{language === 'it' ? 'Naviga' : 'Navigate'}</ColumnTitle>
            <ColumnContent>
              <FooterLink to="/">{language === 'it' ? 'Home' : 'Home'}</FooterLink>
              <FooterLink to="/about">{language === 'it' ? 'Chi Siamo' : 'About Us'}</FooterLink>
              <FooterLink to="/contact">{language === 'it' ? 'Contatti' : 'Contact'}</FooterLink>
            </ColumnContent>
          </FooterColumn>
          
          <FooterColumn>
            <ColumnTitle>{language === 'it' ? 'Servizi' : 'Services'}</ColumnTitle>
            <ColumnContent>
              <FooterLink to="/services/short-term-rentals">
                <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <HotelIcon /> {language === 'it' ? 'Affitti Brevi' : 'Short-term Rentals'}
                </span>
              </FooterLink>
              <FooterLink to="/services/long-term-rentals">
                <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <HomeIcon /> {language === 'it' ? 'Affitti Medio/Lungo Termine' : 'Medium/Long-term Rentals'}
                </span>
              </FooterLink>
              <FooterLink to="/services/auctions">
                <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <AuctionIcon /> {language === 'it' ? 'Aste' : 'Auctions'}
                </span>
              </FooterLink>
              <FooterLink to="/services/buy-sell">
                <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <MapPinIcon /> {language === 'it' ? 'Compravendite' : 'Buy & Sell'}
                </span>
              </FooterLink>
              <FooterLink to="/services/events">
                <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <EventIcon /> {language === 'it' ? 'Eventi' : 'Events'}
                </span>
              </FooterLink>
              <FooterLink to="/services/experiences">
                <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <ExperienceIcon /> {language === 'it' ? 'Servizi/Experience' : 'Services/Experience'}
                </span>
              </FooterLink>
            </ColumnContent>
          </FooterColumn>
          
          <FooterColumn>
            <ColumnTitle>{language === 'it' ? 'Contatti' : 'Contact'}</ColumnTitle>
            <ColumnContent>
              <ContactItem>
                <PhoneIcon />
                <ContactText>0039 3534219165</ContactText>
              </ContactItem>
              <ContactItem>
                <MailIcon />
                <ContactText>info@j-o-h.com</ContactText>
              </ContactItem>
            </ColumnContent>
          </FooterColumn>
        </TopSection>
        
        <Divider />
        
        <BottomSection>
          <Copyright>
            © {new Date().getFullYear()} J-O-H {language === 'it' ? 'Tutti i diritti riservati.' : 'All rights reserved.'}
          </Copyright>
          
          <LegalLinks>
            <LegalLink to="/terms-of-service">{language === 'it' ? 'Termini di Servizio' : 'Terms of Service'}</LegalLink>
            <LegalLink to="/privacy-policy">{language === 'it' ? 'Privacy Policy' : 'Privacy Policy'}</LegalLink>
            <LegalLink to="/cookie-policy">{language === 'it' ? 'Cookie Policy' : 'Cookie Policy'}</LegalLink>
          </LegalLinks>
        </BottomSection>
      </FooterInner>
      
      <BackToTopButton onClick={scrollToTop}>
        <ArrowUpIcon />
      </BackToTopButton>
    </FooterContainer>
  );
};

export default Footer; 